/**
 * StaffDetailDrawer
 *
 * Side drawer for inspecting an EventStaffAssignment row. Surfaces the
 * status-workflow buttons (Check In / Release) at the bottom and an
 * inline Edit section for role + clearing the station assignment.
 * Picking a specific station during reunification flows through the
 * `assign_station` action; this drawer has no station picker because
 * the station list lives behind a deep nested route the SMS app doesn't
 * surface.
 *
 * Staff notes live in the typed ReunificationNote table (ADR-121 P3) and
 * render via the shared NotesPanel; EventStaffAssignment.notes is legacy.
 */

import { useEffect, useMemo, useState } from 'react';
import { Box, Field, HStack, VStack } from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  SectionCard,
  SelectInput,
} from '@themes/aegix';
import { DrawerComponent } from '../../ui/drawerComponent';
import { DetailRow } from '../DetailRow';
import { toaster } from '../../ui/toaster';
import {
  useCheckInStaff,
  useReleaseStaff,
  useUpdateEventStaff,
} from '../../../hooks/queries/useReunification';
import { useFeature } from '../../../hooks/useFeature';
import { FeatureCode } from '../../../types/features';
import {
  STAFF_ROLE_OPTIONS,
  type StaffRole,
} from '../../../types/ReunificationTypes';
import { NotesPanel } from '../../reunification/NotesPanel';
import type { StatusType } from '@themes/aegix';

interface StaffRowData {
  id: string;
  name: string;
  role: string;
  station: string;
  status: StatusType;
  status_label: string;
  checked_in: string;
  raw_status: string;
  raw_role: string;
  /**
   * Legacy notes string kept in the row shape for backward compat with
   * EventStaffPanel's transformer. Not consumed by this drawer.
   */
  raw_notes: string;
  has_station: boolean;
}

interface StaffDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  staff: StaffRowData | undefined;
}

export function StaffDetailDrawer({
  isOpen,
  onClose,
  eventId,
  staff,
}: StaffDetailDrawerProps) {
  const checkInMutation = useCheckInStaff(eventId);
  const releaseMutation = useReleaseStaff(eventId);
  const updateMutation = useUpdateEventStaff(eventId);

  // Same feature gate as the Add button on EventStaffPanel. The API
  // enforces `CanManageStaffAssignments`; this flag just hides Edit when
  // reunification is off site-wide.
  const canManageStaff = useFeature(FeatureCode.REUNIFICATION);

  const [editing, setEditing] = useState(false);
  const [editRole, setEditRole] = useState('');

  const canCheckIn = staff && ['assigned'].includes(staff.raw_status);
  const canRelease =
    staff && ['checked_in', 'active'].includes(staff.raw_status);

  // Reset edit state whenever the drawer opens on a new row, or closes.
  useEffect(() => {
    if (isOpen && staff) {
      setEditing(false);
      setEditRole(staff.raw_role);
    } else if (!isOpen) {
      setEditing(false);
    }
  }, [isOpen, staff]);

  const hasChanges = useMemo(() => {
    if (!staff) return false;
    return editRole !== staff.raw_role;
  }, [staff, editRole]);

  const handleCheckIn = () => {
    if (!staff) return;
    checkInMutation.mutate(staff.id, {
      onSuccess: () => {
        toaster.create({ title: 'Staff checked in', type: 'success' });
        onClose();
      },
      onError: (err) => {
        toaster.create({
          title: 'Failed to check in staff',
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleRelease = () => {
    if (!staff) return;
    releaseMutation.mutate(staff.id, {
      onSuccess: () => {
        toaster.create({ title: 'Staff released', type: 'success' });
        onClose();
      },
      onError: (err) => {
        toaster.create({
          title: 'Failed to release staff',
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleEditClick = () => {
    if (!staff) return;
    setEditRole(staff.raw_role);
    setEditing(true);
  };

  const handleCancelEdit = () => {
    if (staff) {
      setEditRole(staff.raw_role);
    }
    setEditing(false);
  };

  const handleSave = () => {
    if (!staff) return;
    const data: Partial<{ role: StaffRole }> = {};
    if (editRole !== staff.raw_role) data.role = editRole as StaffRole;
    if (Object.keys(data).length === 0) {
      setEditing(false);
      return;
    }
    updateMutation.mutate(
      { staffId: staff.id, data },
      {
        onSuccess: () => {
          toaster.create({ title: 'Staff updated', type: 'success' });
          setEditing(false);
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to update staff',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const handleClearStation = () => {
    if (!staff) return;
    updateMutation.mutate(
      { staffId: staff.id, data: { station_assignment: null } },
      {
        onSuccess: () => {
          toaster.create({ title: 'Station cleared', type: 'success' });
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to clear station',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Staff Detail"
      size="md"
      closeOnOutside={false}
      closeOnEsc={false}
      footer={
        <HStack gap={3} width="100%">
          {canCheckIn && (
            <ButtonComponent
              visual="primary"
              onClick={handleCheckIn}
              disabled={checkInMutation.isPending}
              data-e2e="check-in-button">
              {checkInMutation.isPending ? 'Checking In...' : 'Check In'}
            </ButtonComponent>
          )}
          {canRelease && (
            <ButtonComponent
              visual="alert"
              onClick={handleRelease}
              disabled={releaseMutation.isPending}
              data-e2e="release-button">
              {releaseMutation.isPending ? 'Releasing...' : 'Release'}
            </ButtonComponent>
          )}
          <ButtonComponent visual="secondary" onClick={onClose} data-e2e="close-button">
            Close
          </ButtonComponent>
        </HStack>
      }
    >
      {staff ? (
        <VStack align="stretch" gap={4}>
          <SectionCard title="Staff Details">
            <VStack align="stretch" gap={0}>
              <DetailRow label="Name" value={staff.name} dataE2e="staff-detail-name" />
              <DetailRow label="Role" value={staff.role} dataE2e="staff-detail-role" />
              <DetailRow label="Station" value={staff.station} dataE2e="staff-detail-station" />
              <DetailRow label="Status" value={staff.status_label} dataE2e="staff-detail-status" />
              <DetailRow label="Checked In" value={staff.checked_in} dataE2e="staff-detail-checked-in" />
            </VStack>
          </SectionCard>

          {/* Typed/tagged notes via NotesPanel (ADR-121 P3). Server is
              the source of truth for visibility. */}
          <NotesPanel
            eventId={eventId}
            subject={{ staff: staff.id }}
            e2ePrefix="staff-notes-panel"
          />

          {canManageStaff && !editing && (
            <HStack justify="flex-end" gap={2}>
              {staff.has_station && (
                <ButtonComponent
                  visual="secondary"
                  size="sm"
                  onClick={handleClearStation}
                  disabled={updateMutation.isPending}
                  data-e2e="staff-clear-station-button"
                >
                  {updateMutation.isPending
                    ? 'Clearing…'
                    : 'Clear station assignment'}
                </ButtonComponent>
              )}
              <ButtonComponent
                visual="secondary"
                size="sm"
                onClick={handleEditClick}
                data-e2e="staff-edit-button"
              >
                Edit details
              </ButtonComponent>
            </HStack>
          )}

          {canManageStaff && editing && (
            <SectionCard title="Edit details">
              <VStack align="stretch" gap={4}>
                <SelectInput
                  label="Role"
                  name="staff_edit_role"
                  value={editRole}
                  onChange={(val: string) => setEditRole(val)}
                  option={STAFF_ROLE_OPTIONS}
                  placeholder="Select a role"
                  e2eTitle="staff-edit-role"
                />

                <Field.HelperText fontSize="xs" color="text.secondary">
                  Status (assigned / checked-in / released) changes via
                  the Check In and Release buttons — those carry the
                  AAS-712 broadcast and AAS-902 chat join.
                </Field.HelperText>

                {staff.has_station && (
                  <Box>
                    <Field.Label fontSize="sm" color="text.primary">
                      Station assignment
                    </Field.Label>
                    <HStack gap={2} mt={1}>
                      <Box fontSize="sm" color="text.secondary">
                        {staff.station}
                      </Box>
                      <ButtonComponent
                        visual="secondary"
                        size="sm"
                        onClick={handleClearStation}
                        disabled={updateMutation.isPending}
                        data-e2e="staff-edit-clear-station"
                      >
                        Clear
                      </ButtonComponent>
                    </HStack>
                  </Box>
                )}

                <HStack justify="flex-end" gap={2}>
                  <ButtonComponent
                    visual="secondary"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={updateMutation.isPending}
                    data-e2e="staff-edit-cancel"
                  >
                    Cancel
                  </ButtonComponent>
                  <ButtonComponent
                    visual="primary"
                    size="sm"
                    onClick={handleSave}
                    disabled={updateMutation.isPending || !hasChanges}
                    data-e2e="staff-edit-save"
                  >
                    {updateMutation.isPending ? 'Saving…' : 'Save'}
                  </ButtonComponent>
                </HStack>
              </VStack>
            </SectionCard>
          )}
        </VStack>
      ) : null}
    </DrawerComponent>
  );
}
